import React, { useState, useEffect } from 'react';
import { useFlowsStore, normalizeFlow } from '../store/flowsStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Play, ArrowUp, ArrowDown, X, Copy, Workflow } from 'lucide-react';
import axiosInstance from '../lib/axios';

// T-234: modal de gestión + ejecución de Flujos IA. La EJECUCIÓN se delega al
// orquestador de macros de EditorPage (T-231): un flujo tiene el mismo shape
// { title, steps: [{ instruction, model, advisors }] } que un comando macro,
// así que al pulsar Ejecutar solo se llama onRun(flow) y el padre lo pasa
// DIRECTO a handleQuickAction('macro', flow, baseText).
const FlowsModal = ({ open, onClose, onRun }) => {
  const { flows, addFlow, updateFlow, deleteFlow } = useFlowsStore();
  // T-234: el formulario vive aparte de la lista; `formOpen` alterna entre la
  // lista de flujos y el editor crear/editar.
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', steps: [{ instruction: '', model: '', advisors: [] }] });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, title: '' });
  // T-234: catálogo de modelos disponibles para asignar por paso (modelo +
  // asesores). Se carga al abrir el modal; si la lista es vacía los controles
  // nuevos no se renderizan (los pasos usan el modelo por defecto).
  const [chatModels, setChatModels] = useState([]);

  useEffect(() => {
    if (!open) return;
    axiosInstance.get('/ai/chat-models')
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.models || []);
        setChatModels(list);
      })
      .catch(() => setChatModels([]));
  }, [open]);

  const openCreateForm = () => {
    setEditingId(null);
    setFormData({ title: '', steps: [{ instruction: '', model: '', advisors: [] }] });
    setFormOpen(true);
  };

  const openEditForm = (flow) => {
    setEditingId(flow.id);
    const steps = Array.isArray(flow.steps) && flow.steps.length > 0
      ? flow.steps.map((s) => ({ instruction: s.instruction || '', model: s.model || '', advisors: Array.isArray(s.advisors) ? s.advisors.slice(0, 3) : [] }))
      : [{ instruction: '', model: '', advisors: [] }];
    setFormData({ title: flow.title || '', steps });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setFormData({ title: '', steps: [{ instruction: '', model: '', advisors: [] }] });
  };

  // T-234: editores de paso (add / remove / reorder / edit) — espejo del patrón
  // de CommandManagement, pero sin PromptImprover ni categoría ni chatTemplate.
  const addStep = () => {
    setFormData((prev) => ({ ...prev, steps: [...prev.steps, { instruction: '', model: '', advisors: [] }] }));
  };

  const removeStep = (index) => {
    setFormData((prev) => {
      if (prev.steps.length <= 1) return prev; // mínimo 1 paso siempre
      return { ...prev, steps: prev.steps.filter((_, i) => i !== index) };
    });
  };

  const updateStep = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.map((s, i) => (i === index ? { ...s, instruction: value } : s))
    }));
  };

  const updateStepField = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    }));
  };

  const moveStep = (index, direction) => {
    setFormData((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.steps.length) return prev;
      const steps = [...prev.steps];
      [steps[index], steps[target]] = [steps[target], steps[index]];
      return { ...prev, steps };
    });
  };

  const handleSave = () => {
    // T-234: descartar pasos sin instrucción pero conservar al menos 1.
    const cleanedSteps = formData.steps
      .map((s) => ({ instruction: (s.instruction || '').trim(), model: s.model || '', advisors: Array.isArray(s.advisors) ? s.advisors.slice(0, 3) : [] }))
      .filter((s) => s.instruction.length > 0);

    if (!formData.title.trim() || cleanedSteps.length === 0) {
      toast.error('Título y al menos un paso con instrucción son requeridos');
      return;
    }

    const payload = { title: formData.title.trim(), steps: cleanedSteps };

    if (editingId) {
      updateFlow(editingId, payload);
      toast.success('Flujo actualizado');
    } else {
      addFlow(payload);
      toast.success('Flujo creado');
    }

    closeForm();
  };

  const handleDelete = (flow) => {
    setDeleteConfirm({ open: true, id: flow.id, title: flow.title || '' });
  };

  const confirmDelete = () => {
    deleteFlow(deleteConfirm.id);
    toast.success('Flujo eliminado');
    setDeleteConfirm({ open: false, id: null, title: '' });
  };

  const handleDuplicate = (flow) => {
    const steps = Array.isArray(flow.steps) && flow.steps.length > 0
      ? flow.steps.map((s) => ({ instruction: s.instruction || '', model: s.model || '', advisors: Array.isArray(s.advisors) ? s.advisors.slice(0, 3) : [] }))
      : [{ instruction: '', model: '', advisors: [] }];
    addFlow({ title: `${flow.title || ''} (copia)`, steps });
    toast.success('Flujo duplicado');
  };

  const handleRun = (flow) => {
    // T-234: el padre (EditorPage) cierra el modal y pasa el flujo normalizado
    // al orquestador de macros (T-231) — mismo shape que un comando macro.
    onRun(normalizeFlow(flow));
  };

  // T-234: un flujo es ejecutable solo si tiene al menos un paso con instrucción.
  const flowHasInstruction = (flow) =>
    Array.isArray(flow.steps) && flow.steps.some((s) => (s.instruction || '').trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="flows-modal" className="glass border-0 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Workflow className="w-5 h-5 text-emerald-500" />
            Flujos IA
          </DialogTitle>
          <DialogDescription className="sr-only">Gestiona y ejecuta flujos IA.</DialogDescription>
        </DialogHeader>

        {/* T-234: microcopy literal — distingue Flujo de Plantilla. */}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Un flujo ejecuta una secuencia de pasos, uno tras otro, sobre el texto seleccionado (o sin texto base si no hay selección). Cada paso puede usar su propio modelo y consultar hasta 3 modelos asesores: los asesores responden primero y su respuesta se entrega como insumo al modelo del paso. Cada llamada — paso o asesor — consume créditos según su modelo. Distinto de una Plantilla: la plantilla solo da estilo al chat, no ejecuta nada.
        </p>

        {!formOpen && (
          <>
            <div className="flex justify-end">
              <Button
                data-testid="add-flow-button"
                onClick={openCreateForm}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Flujo
              </Button>
            </div>

            {flows.length === 0 ? (
              <div className="text-center py-10">
                <Workflow className="w-14 h-14 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-600 dark:text-slate-300 mb-4">No hay flujos configurados</p>
                <Button onClick={openCreateForm}>Crear primer flujo</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {flows.map((flow) => (
                  <Card key={flow.id} data-testid={`flow-card-${flow.id}`} className="glass border-0 hover:shadow-lg transition-all">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex flex-col gap-1 min-w-0">
                          <h3 className="font-semibold text-lg truncate">{flow.title}</h3>
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800 w-fit">
                            {(flow.steps || []).length} paso(s)
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            data-testid={`run-flow-${flow.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRun(flow)}
                            disabled={!flowHasInstruction(flow)}
                            className="h-8 w-8 p-0 hover:bg-emerald-100 hover:text-emerald-600 dark:hover:bg-emerald-900/50 dark:hover:text-emerald-300"
                            title="Ejecutar flujo"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`edit-flow-${flow.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditForm(flow)}
                            className="h-8 w-8 p-0 hover:bg-sky-100 dark:hover:bg-sky-900/50"
                            title="Editar flujo"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`duplicate-flow-${flow.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicate(flow)}
                            className="h-8 w-8 p-0 hover:bg-sky-100 dark:hover:bg-sky-900/50"
                            title="Duplicar flujo"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`delete-flow-${flow.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(flow)}
                            className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/50 dark:hover:text-red-300"
                            title="Eliminar flujo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {formOpen && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Título del flujo</label>
              <Input
                data-testid="flow-title-input"
                placeholder="Ej: Revisión con asesores"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium">
                  {formData.steps.length > 1
                    ? `Pasos del flujo (${formData.steps.length})`
                    : 'Instrucción del paso'}
                </label>
                {formData.steps.length > 1 && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    Se ejecutan en orden; cada paso recibe el resultado del anterior.
                  </span>
                )}
              </div>

              {formData.steps.map((step, index) => (
                <div
                  key={index}
                  data-testid={`flow-step-${index}`}
                  className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      Paso {index + 1}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        data-testid={`flow-step-up-${index}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => moveStep(index, -1)}
                        disabled={index === 0}
                        className="h-7 w-7 p-0"
                        title="Subir paso"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        data-testid={`flow-step-down-${index}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => moveStep(index, 1)}
                        disabled={index === formData.steps.length - 1}
                        className="h-7 w-7 p-0"
                        title="Bajar paso"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        data-testid={`flow-step-remove-${index}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStep(index)}
                        disabled={formData.steps.length <= 1}
                        className="h-7 w-7 p-0 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/50 dark:hover:text-red-300"
                        title="Eliminar paso"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    data-testid={`flow-step-instruction-${index}`}
                    placeholder="Ej: Reescribe el texto seleccionado en tono formal…"
                    value={step.instruction}
                    onChange={(e) => updateStep(index, e.target.value)}
                    rows={4}
                    className="resize-none"
                  />
                  {chatModels.length > 0 ? (
                    <div className="space-y-2 pt-1">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Modelo del paso</label>
                        <select value={step.model || ''} onChange={(e) => updateStepField(index, 'model', e.target.value)}
                          data-testid={`flow-step-model-${index}`}
                          className="w-full h-9 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 text-sm">
                          <option value="">Modelo por defecto</option>
                          {chatModels.map((m) => <option key={m.model_id} value={m.model_id}>{m.model_id}</option>)}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                          Asesores (máx 3)
                          <span className="ml-1 text-slate-400 dark:text-slate-500">({(step.advisors || []).length}/3)</span>
                        </label>
                        <div className="flex flex-wrap gap-1">
                          {chatModels.map((m) => {
                            const active = (step.advisors || []).includes(m.model_id);
                            const atLimit = (step.advisors || []).length >= 3 && !active;
                            return (
                              <button
                                key={m.model_id}
                                type="button"
                                data-testid={`flow-step-advisor-${index}-${m.model_id}`}
                                disabled={atLimit}
                                onClick={() => {
                                  const current = step.advisors || [];
                                  const next = active
                                    ? current.filter((id) => id !== m.model_id)
                                    : [...current, m.model_id].slice(0, 3);
                                  updateStepField(index, 'advisors', next);
                                }}
                                className={
                                  'inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ' +
                                  (active
                                    ? 'border-sky-400 bg-sky-100 text-sky-700 dark:border-sky-600 dark:bg-sky-900/50 dark:text-sky-200'
                                    : 'border-slate-300 bg-white text-slate-600 hover:border-sky-300 hover:bg-sky-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-sky-700 dark:hover:bg-sky-900/30')
                                }
                              >
                                {m.model_id}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      El administrador aún no ha curado modelos de chat; los pasos usarán el modelo por defecto.
                    </p>
                  )}
                </div>
              ))}

              <Button
                type="button"
                data-testid="flow-add-step-button"
                variant="outline"
                size="sm"
                onClick={addStep}
                className="w-full glass border-0"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar paso
              </Button>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={closeForm}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                data-testid="save-flow-button"
                onClick={handleSave}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              >
                {editingId ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>

      {/* T-234: confirmación de borrado (ConfirmDialog, sin prop `message`). */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(o) => { if (!o) setDeleteConfirm({ open: false, id: null, title: '' }); }}
        title="Eliminar flujo"
        description={`¿Estás seguro de que deseas eliminar el flujo "${deleteConfirm.title}"?`}
        onConfirm={confirmDelete}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
      />
    </Dialog>
  );
};

export default FlowsModal;
